import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MetricsTourProps {
  isOpen: boolean;
  onClose: () => void;
  metrics?: Array<{
    key: string;
    name: string;
    emoji: string;
    unit: string;
    type: "numeric" | "boolean";
  }>;
}

// Mini graph component for tour demonstrations
const MiniGraph = ({
 
  data, 
  label, 
  type = "numeric",
  inverse = false,
  highlightDay,
  highlightColumn,
  colorScheme = "green",
}: { 
  data: number[][]; 
  label: string; 
  type?: "numeric" | "boolean";
  inverse?: boolean;
  highlightDay?: { week: number; day: number };
  highlightColumn?: number;
  colorScheme?: "green" | "red" | "blue" | "purple" | "orange" | "pink";
}) => {
  const { t } = useTranslation();
  const getColor = (value: number) => {
    // Handle no data
    if (value === -1) {
      return "bg-gray-300 dark:bg-gray-700 opacity-40";
    }
    
    if (type === "boolean") {
      // Boolean: 0 = not done (gray), 1 = done (color)
      if (inverse) {
        return value === 0 
          ? "bg-green-500 dark:bg-green-400" 
          : "bg-red-500 dark:bg-red-400";
      }
      return value === 0 
        ? "bg-gray-300 dark:bg-gray-700 opacity-40" 
        : "bg-green-500 dark:bg-green-400";
    }
    
    // Numeric: calculate intensity (0-4)
    let intensity = Math.floor((value / 100) * 4);
    intensity = Math.max(0, Math.min(4, intensity));
    
    if (inverse) {
      // Inverse colors: low values = good (green), high values = bad (red)
      const colors = [
        "bg-green-500 dark:bg-green-400",
        "bg-green-500 dark:bg-green-400",
        "bg-yellow-400 dark:bg-yellow-500", 
        "bg-orange-500 dark:bg-orange-600",
        "bg-red-500 dark:bg-red-600",
      ];
      return colors[intensity];
    }
    
    // Color schemes for rainbow effect
    const colorSchemes = {
      green: [
        "bg-[hsl(var(--contrib-0))]",
        "bg-[hsl(var(--contrib-1))]",
        "bg-[hsl(var(--contrib-2))]",
        "bg-[hsl(var(--contrib-3))]",
        "bg-[hsl(var(--contrib-4))]",
      ],
      red: [
        "bg-gray-200 dark:bg-gray-800",
        "bg-red-200 dark:bg-red-900",
        "bg-red-300 dark:bg-red-700",
        "bg-red-400 dark:bg-red-600",
        "bg-red-500 dark:bg-red-500",
      ],
      blue: [
        "bg-gray-200 dark:bg-gray-800",
        "bg-blue-200 dark:bg-blue-900",
        "bg-blue-300 dark:bg-blue-700",
        "bg-blue-400 dark:bg-blue-600",
        "bg-blue-500 dark:bg-blue-500",
      ],
      purple: [
        "bg-gray-200 dark:bg-gray-800",
        "bg-purple-200 dark:bg-purple-900",
        "bg-purple-300 dark:bg-purple-700",
        "bg-purple-400 dark:bg-purple-600",
        "bg-purple-500 dark:bg-purple-500",
      ],
      orange: [
        "bg-gray-200 dark:bg-gray-800",
        "bg-orange-200 dark:bg-orange-900",
        "bg-orange-300 dark:bg-orange-700",
        "bg-orange-400 dark:bg-orange-600",
        "bg-orange-500 dark:bg-orange-500",
      ],
      pink: [
        "bg-gray-200 dark:bg-gray-800",
        "bg-pink-200 dark:bg-pink-900",
        "bg-pink-300 dark:bg-pink-700",
        "bg-pink-400 dark:bg-pink-600",
        "bg-pink-500 dark:bg-pink-500",
      ],
    };
    
    return colorSchemes[colorScheme][intensity];
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-sm font-semibold text-foreground">{label}</div>
      <div className="flex gap-[3px]">
        {data.map((week, weekIdx) => (
          <div key={weekIdx} className="flex flex-col gap-[3px]">
            {week.map((value, dayIdx) => {
              const isHighlightedDay = 
                highlightDay?.week === weekIdx && highlightDay?.day === dayIdx;
              const isHighlightedColumn = highlightColumn === weekIdx;
              
              return (
                <motion.div
                  key={dayIdx}
                  className={`w-3 h-3 rounded-sm ${getColor(value)} transition-all ${
                    isHighlightedDay ? "ring-2 ring-primary scale-125" : ""
                  } ${isHighlightedColumn ? "ring-2 ring-blue-500" : ""}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: (weekIdx * 7 + dayIdx) * 0.015 }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export const MetricsTour = ({ isOpen, onClose, metrics }: MetricsTourProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to step 0 when tour opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Generate sample data for demonstrations
  const generateWeekData = (weeks: number = 8): number[][] => {
    return Array.from({ length: weeks }, (_, weekIdx) =>
      Array.from({ length: 7 }, (_, dayIdx) => {
        // Randomly create some days with no data (20% chance)
        if (Math.random() < 0.2) {
          return -1; // No data
        }
        // Create varied but realistic patterns
        const baseValue = 50 + Math.sin(weekIdx * 0.5) * 30;
        const dailyVariation = Math.sin(dayIdx * 0.8) * 20;
        return Math.max(0, Math.min(100, baseValue + dailyVariation + Math.random() * 20));
      })
    );
  };

  const generateBooleanData = (weeks: number = 8): number[][] => {
    return Array.from({ length: weeks }, () =>
      Array.from({ length: 7 }, () => {
        // 20% chance of no data
        if (Math.random() < 0.2) {
          return -1;
        }
        return Math.random() > 0.3 ? 1 : 0;
      })
    );
  };

  const generateSparseBooleanData = (weeks: number = 8): number[][] => {
    return Array.from({ length: weeks }, () =>
      Array.from({ length: 7 }, () => {
        // 65% chance of no data (sparsely populated)
        if (Math.random() < 0.65) {
          return -1;
        }
        return Math.random() > 0.4 ? 1 : 0;
      })
    );
  };

  const generateAccumulativeData = (): number[][] => {
    return Array.from({ length: 8 }, (_, weekIdx) =>
      Array.from({ length: 7 }, (_, dayIdx) => {
        // Accumulative pattern - gradually increases
        return Math.min(100, (weekIdx * 7 + dayIdx) * 2 + Math.random() * 10);
      })
    );
  };

  const generateFixedRangeData = (): number[][] => {
    return Array.from({ length: 8 }, () =>
      Array.from({ length: 7 }, () => {
        // 20% chance of no data
        if (Math.random() < 0.2) {
          return -1;
        }
        // Fixed range - stays within bounds
        return 40 + Math.random() * 40; // 40-80 range
      })
    );
  };

  const generateAboveIsBadData = (): number[][] => {
    return Array.from({ length: 8 }, (_, weekIdx) =>
      Array.from({ length: 7 }, (_, dayIdx) => {
        // 20% chance of no data
        if (Math.random() < 0.2) {
          return -1;
        }
        // Some days exceed the limit (bad)
        const baseValue = 60;
        const spike = Math.random() > 0.7 ? 50 : 0;
        return Math.min(100, baseValue + spike + Math.random() * 10);
      })
    );
  };

  const generateGreenToRedData = (): number[][] => {
    // Generate data where most days are in the green range (low values)
    // Only a few days exceed into yellow/orange/red territory
    return Array.from({ length: 8 }, (_, weekIdx) =>
      Array.from({ length: 7 }, (_, dayIdx) => {
        // 15% chance of no data
        if (Math.random() < 0.15) {
          return -1;
        }
        
        // 75% of days stay in green range (0-49 = intensity 0-1)
        // 15% go into yellow (50-74 = intensity 2)
        // 7% go into orange (75-89 = intensity 3)
        // 3% hit red (90-100 = intensity 4)
        const rand = Math.random();
        if (rand < 0.75) {
          // Green range - well under limit
          return Math.random() * 50;
        } else if (rand < 0.90) {
          // Yellow range - approaching limit
          return 50 + Math.random() * 25;
        } else if (rand < 0.97) {
          // Orange range - at limit
          return 75 + Math.random() * 15;
        } else {
          // Red range - over limit
          return 90 + Math.random() * 10;
        }
      })
    );
  };

  const generateBadHabitData = (): number[][] => {
    // Generate data for bad habits (like smoking)
    // Only show 1s (bad/red) or -1 (no data/gray), never 0s (good/green)
    return Array.from({ length: 8 }, () =>
      Array.from({ length: 7 }, () => {
        // 70% chance of no data (didn't smoke)
        if (Math.random() < 0.70) {
          return -1;
        }
        // 30% smoked (show red)
        return 1;
      })
    );
  };

  const steps = [
    {
      title: t('metrics_tour.steps.tracking'),
      content: (
        <div className="space-y-6">
          <p className="text-base text-muted-foreground text-center">{t('ui.text.each_metric_gets_a_visual_chart')}</p>
          <div className="grid grid-cols-2 gap-4 px-2">
            {metrics && metrics.length > 0 ? (
              // Show user's actual metrics (up to 4)
              metrics.slice(0, 4).map((metric, idx) => {
                const colorSchemes = ["orange", "green", "purple", "blue", "pink"] as const;
                return (
                  <MiniGraph 
                    key={metric.key}
                    data={metric.type === "boolean" ? generateBooleanData() : generateWeekData()} 
                    label={`${metric.emoji} ${metric.name}`}
                    type={metric.type}
                    colorScheme={metric.type === "boolean" ? "green" : colorSchemes[idx % colorSchemes.length]}
                  />
                );
              })
            ) : (
              // Fallback to example metrics if none provided
              <>
                <MiniGraph 
                  data={generateWeekData()} 
                  label={`🍕 ${t('metrics_tour.instructions.pizza_slices')}`}
                  type="numeric"
                  colorScheme="orange"
                />
                <MiniGraph 
                  data={generateBooleanData()} 
                  label={`🦷 ${t('metrics_tour.instructions.flossed')}`}
                  type="boolean"
                />
                <MiniGraph 
                  data={generateBooleanData()} 
                  label={`🧘 ${t('metrics_tour.instructions.touched_grass')}`}
                  type="boolean"
                />
                <MiniGraph 
                  data={generateWeekData()} 
                  label={`💻 ${t('metrics_tour.instructions.coding')}`}
                  type="numeric"
                  colorScheme="purple"
                />
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.layout'),
      content: (
        <div className="space-y-6">
          <p className="text-base text-muted-foreground text-center">
            <span className="font-bold text-primary">{t('ui.text.square')}</span> = {t('ui.text.day')} · <span className="font-bold text-blue-500">{t('ui.text.column')}</span> = {t('ui.text.week')}
          </p>
          <div className="flex justify-center">
            <MiniGraph 
              data={generateWeekData()} 
              label={`📈 ${t('metrics_tour.instructions.example_metric')}`}
              type="numeric"
              highlightDay={{ week: 2, day: 3 }}
              highlightColumn={5}
            />
          </div>
          <div className="text-sm text-muted-foreground text-center space-y-1">
            <div>
              <span className="inline-block w-3 h-3 rounded-sm bg-primary mr-2 align-middle" />{t('ui.text.one_day')}</div>
            <div>
              <span className="inline-block w-3 h-8 border-2 border-blue-500 rounded-sm mr-2 align-middle" />{t('ui.text.one_week')}</div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.track_daily'),
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="text-3xl">📷</div>
                <div className="text-3xl">🎤</div>
                <div className="text-3xl">✍️</div>
              </div>
              
              {/* Scrolling Metrics Animation */}
              <div className="relative overflow-hidden h-20 w-full mt-4">
                <motion.div
                  animate={{
                    x: [0, -300],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="flex gap-4 absolute"
                >
                  {(() => {
                    const metrics = [
                      { emoji: "🔥", progress: 85 },
                      { emoji: "🍗", progress: 60 },
                      { emoji: "🏃", progress: 45 },
                      { emoji: "💪", progress: 92 },
                      { emoji: "😴", progress: 75 },
                      { emoji: "💧", progress: 40 },
                    ];
                    
                    const getProgressColor = (progress: number) => {
                      if (progress >= 100) return "rgb(34, 197, 94)";
                      if (progress >= 75) return "rgb(132, 204, 22)";
                      if (progress >= 50) return "rgb(234, 179, 8)";
                      if (progress >= 25) return "rgb(249, 115, 22)";
                      return "rgb(239, 68, 68)";
                    };
                    
                    return [...metrics, ...metrics, ...metrics, ...metrics].map((metric, idx) => (
                      <div key={idx} className="flex-shrink-0 relative">
                        <div 
                          className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-2xl relative overflow-hidden border border-border"
                        >
                          <svg className="absolute inset-0 w-full h-full -rotate-90">
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              fill="none"
                              stroke={getProgressColor(metric.progress)}
                              strokeWidth="4"
                              style={{
                                strokeDasharray: `${2 * Math.PI * 28}`,
                                strokeDashoffset: `${2 * Math.PI * 28 * (1 - metric.progress / 100)}`,
                              }}
                              opacity="0.9"
                            />
                          </svg>
                          <span className="relative z-10">{metric.emoji}</span>
                        </div>
                      </div>
                    ));
                  })()}
                </motion.div>
                
                <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
                <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              </div>
            </div>
            
            <div className="bg-card rounded-lg p-4 border border-border space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-sm bg-green-500 flex-shrink-0" />
                <div className="text-sm text-muted-foreground">{t('ui.text.log_history_stored_on_each_square')}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 rounded-sm bg-green-500" />
                  <div className="absolute -top-1 -right-1 text-base">📸</div>
                </div>
                <div className="text-sm text-muted-foreground">{t('ui.text.progress_pics_too')}</div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "",
      content: (
        <div className="space-y-8">
          <div className="space-y-6">
            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-sm text-muted-foreground">{t('metrics_tour.instructions.chart_less')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-sm bg-[hsl(var(--contrib-0))]" />
                  <div className="w-7 h-7 rounded-sm bg-[hsl(var(--contrib-1))]" />
                  <div className="w-7 h-7 rounded-sm bg-[hsl(var(--contrib-2))]" />
                  <div className="w-7 h-7 rounded-sm bg-[hsl(var(--contrib-3))]" />
                  <div className="w-7 h-7 rounded-sm bg-[hsl(var(--contrib-4))]" />
                </div>
                <span className="text-sm text-muted-foreground">{t('metrics_tour.instructions.chart_more')}</span>
              </div>
            </div>
            <div className="bg-card rounded-lg p-6 border border-border">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-sm text-muted-foreground">{t('ui.text.not_done')}</span>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-sm bg-gray-300 dark:bg-gray-700 opacity-40" />
                  <div className="w-10 h-10 rounded-sm bg-green-500 dark:bg-green-400" />
                </div>
                <span className="text-sm text-muted-foreground">{t('metrics_tour.instructions.chart_done')}</span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.limits'),
      content: (
        <div className="space-y-6">
          <p className="text-base text-muted-foreground text-center">
            {t('onboarding.metrics.optional')}
          </p>
          <div className="bg-card rounded-lg p-4 border border-border">
            <MiniGraph 
              data={generateGreenToRedData()} 
              label={`🔥 ${t('metrics_tour.instructions.calories')}`}
              type="numeric"
              inverse={true}
            />
          </div>
          <div className="bg-card rounded-lg p-4 border border-border space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-foreground">{t('metrics_tour.instructions.threshold')}</span>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-sm bg-green-500" />
                <span className="text-xs text-muted-foreground">→</span>
                <div className="w-4 h-4 rounded-sm bg-yellow-400" />
                <span className="text-xs text-muted-foreground">→</span>
                <div className="w-4 h-4 rounded-sm bg-orange-500" />
                <span className="text-xs text-muted-foreground">→</span>
                <div className="w-4 h-4 rounded-sm bg-red-500" />
              </div>
              <span className="text-sm font-semibold text-foreground">{t('ui.text.too_much')}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.habits'),
      content: (
        <div className="space-y-6">
          <div className="space-y-6">
            <div className="bg-card rounded-lg p-4 border border-border">
              <MiniGraph 
                data={generateBooleanData()} 
                label={`🎸 ${t('metrics_tour.instructions.practiced_guitar')}`}
                type="boolean"
                inverse={false}
              />
              <p className="text-xs text-green-600 dark:text-green-400 text-center mt-2">
                ✓ {t('metrics_tour.instructions.yes_good')}
              </p>
            </div>
            <div className="bg-card rounded-lg p-4 border border-border">
              <MiniGraph 
                data={generateBadHabitData()} 
                label={`🚬 ${t('metrics_tour.instructions.smoked_today')}`}
                type="boolean"
                inverse={true}
              />
              <p className="text-xs text-red-600 dark:text-red-400 text-center mt-2">
                ✗ {t('metrics_tour.instructions.yes_bad')}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.modes'),
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-card rounded-lg p-5 border border-border">
              <div className="flex items-start gap-4">
                <div className="text-4xl flex-shrink-0">➕</div>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-foreground mb-1">{t('ui.text.add_up')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t('ui.text.calories_emoji')}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t('ui.text.water_emoji')}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg p-5 border border-border">
              <div className="flex items-start gap-4">
                <div className="text-4xl flex-shrink-0">📏</div>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-foreground mb-1">{t('ui.text.single_value')}</div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t('ui.text.weight_emoji')}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t('ui.text.mood_emoji')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.customization'),
      content: (
        <div className="space-y-8 text-center pt-8">
          <div className="space-y-3">
            <div className="text-7xl">🏆</div>
            <p className="text-lg text-muted-foreground">{t('ui.text.built_in_metrics_on_leaderboards')}</p>
          </div>

          <div className="space-y-3">
            <div className="text-7xl">✏️</div>
            <p className="text-lg text-muted-foreground">{t('ui.text.custom_metrics_give_you')}<span className="font-bold text-primary">{t('ui.text.full_control')}</span>
            </p>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.comparing'),
      content: (
        <div className="space-y-4 text-center">

          {/* Friend comparison */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="text-sm font-semibold mb-3">⚔️ {t('metrics_tour.instructions.you_vs_sarah')}</div>
            <div className="grid grid-cols-7 gap-1 max-w-[200px] mx-auto">
              {[
                { you: 'blue', them: 'gray' },
                { you: 'blue', them: 'gray' },
                { you: 'gray', them: 'red' },
                { you: 'blue', them: 'gray' },
                { you: 'tie', them: 'tie' },
                { you: 'blue', them: 'gray' },
                { you: 'gray', them: 'red' }
              ].map((day, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: i * 0.08 }}
                  className="aspect-square rounded-sm"
                  style={{
                    background: day.you === 'blue' ? 'rgb(59 130 246)' :
                                day.them === 'red' ? 'rgb(239 68 68)' :
                                day.you === 'tie' ? 'rgb(148 163 184)' :
                                'rgb(229 231 235)'
                  }}
                />
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 text-xs mt-3">
              <span className="text-blue-500 font-bold">{t('ui.text.you_4')}</span>
              <span className="text-slate-400">·</span>
              <span className="text-red-500 font-bold">{t('ui.text.them_2')}</span>
            </div>
          </div>

          {/* Self comparison - find correlations */}
          <div className="bg-card rounded-lg p-4 border border-border space-y-3">
            <div className="text-sm font-semibold">
              🔬 Insights using <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">{t('ui.text.compare_tool')}</span>
            </div>
            
            {/* Steps vs Mood example */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">🚶 {t('metrics_tour.instructions.steps')}</span>
                <div className="flex gap-0.5">
                  {[3, 4, 2, 4, 4, 3, 1].map((intensity, i) => (
                    <div
                      key={i}
                      className="w-3 h-8 rounded-sm"
                      style={{
                        background: intensity === 1 ? 'rgb(229 231 235)' :
                                   intensity === 2 ? 'rgb(134 239 172)' :
                                   intensity === 3 ? 'rgb(74 222 128)' :
                                   'rgb(34 197 94)'
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">😊 {t('metrics_tour.instructions.mood')}</span>
                <div className="flex gap-0.5">
                  {[3, 4, 2, 4, 4, 3, 1].map((intensity, i) => (
                    <div
                      key={i}
                      className="w-3 h-8 rounded-sm"
                      style={{
                        background: intensity === 1 ? 'rgb(229 231 235)' :
                                   intensity === 2 ? 'rgb(252 211 77)' :
                                   intensity === 3 ? 'rgb(251 191 36)' :
                                   'rgb(245 158 11)'
                      }}
                    />
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-green-600 dark:text-green-400 text-right">
                ✓ {t('metrics_tour.instructions.strong_match')}
              </p>
            </div>

            {/* Caffeine vs Sleep example */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">☕ {t('metrics_tour.instructions.caffeine')}</span>
                <div className="flex gap-0.5">
                  {[2, 1, 4, 2, 3, 1, 4].map((intensity, i) => (
                    <div
                      key={i}
                      className="w-3 h-8 rounded-sm"
                      style={{
                        background: intensity === 1 ? 'rgb(229 231 235)' :
                                   intensity === 2 ? 'rgb(196 181 253)' :
                                   intensity === 3 ? 'rgb(167 139 250)' :
                                   'rgb(139 92 246)'
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">😴 {t('metrics_tour.instructions.sleep')}</span>
                <div className="flex gap-0.5">
                  {[3, 4, 1, 3, 2, 4, 1].map((intensity, i) => (
                    <div
                      key={i}
                      className="w-3 h-8 rounded-sm"
                      style={{
                        background: intensity === 1 ? 'rgb(229 231 235)' :
                                   intensity === 2 ? 'rgb(147 197 253)' :
                                   intensity === 3 ? 'rgb(96 165 250)' :
                                   'rgb(59 130 246)'
                      }}
                    />
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-orange-600 dark:text-orange-400 text-right">
                ⚠️ {t('metrics_tour.instructions.high_caffeine_poor_sleep')}
              </p>
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground/60 italic">{t('ui.text.coming_soon_ai_will_detect_and_alert_you_about_imp')}</p>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.assistant'),
      content: (
        <div className="space-y-6 text-center pt-8">
          <div className="text-6xl">💬</div>
          <p className="text-lg font-semibold text-foreground">{t('ui.text.track_something_new')}</p>
          <p className="text-base text-muted-foreground">{t('ui.text.just_ask_the_ai')}</p>
          <div className="bg-card rounded-lg p-4 border border-border space-y-3 text-left">
            <div className="text-sm text-muted-foreground italic">
              "{t('metrics_tour.instructions.track_piano')}"
            </div>
            <div className="text-sm text-muted-foreground italic">
              "{t('metrics_tour.instructions.track_water')}"
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('metrics_tour.steps.ready'),
      content: (
        <div className="space-y-6 text-center pt-12">
          <div className="text-6xl">🎉</div>
          <p className="text-xl font-semibold text-foreground">{t('ui.text.ready_to_go')}</p>
          <p className="text-base text-muted-foreground">{t('ui.text.review_and_adjust_your_new_metrics_on_the_next_scr')}</p>
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            onClick={onClose}
          />

          {/* Tour Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-8 max-w-lg mx-auto bg-background rounded-2xl shadow-2xl z-[101] overflow-hidden flex flex-col"
            style={{ 
              bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
              maxHeight: 'calc(100dvh - 10rem)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-border flex-shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-foreground">{t('ui.text.how_it_works')}</span>
                <div className="flex items-center gap-1">
                  {steps.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        idx === currentStep 
                          ? "bg-foreground" 
                          : "bg-muted-foreground/30"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 min-h-0 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3 w-full my-auto"
                >
                  <h2 className="text-xl font-bold text-center">
                    {steps[currentStep].title}
                  </h2>
                  {steps[currentStep].content}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-3 border-t border-border flex-shrink-0">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="text-sm"
              >
                {t('metrics_tour.navigation.back')}
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t('metrics_tour.navigation.later')}
              </Button>
              <Button
                onClick={handleNext}
                className="text-sm px-6 bg-green-600 hover:bg-green-700 text-white"
              >
                {currentStep === steps.length - 1 ? t('metrics_tour.navigation.get_started') : t('metrics_tour.navigation.next')}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
